import { useMemo } from "react";

import { hasArrayElements, getCurrencyCode } from "@/apiUtils/commonUtils";
import IconChevronLeft from "@/assets/icons/chevronLeft.svg?react";
import IconChevronRight from "@/assets/icons/chevronRight.svg?react";
import { HeaderDynamicBannerContent } from "@/common/lib/types";
import { cn } from "@/lib/tailwind";
import { constructUrl } from "@/utils/common";
import { fontLTBukra, lufga } from "@/utils/fonts";
import { CLink } from "../common/customLink/CustomLink";
import DummyClientConsoleComp from "../DummyClientConsoleComp";
import { Carousel } from "../ui/carousel/Carousel";
import CurrencyIcon from "@/common/core/icons/CurrencyIcon";
import React, { ReactNode } from "react";

interface IProps {
  locale: string;
  className?: string;
  content?: HeaderDynamicBannerContent;
}

export const fontLufgaRegex = /^(lufga|lufga font|font lufga)$/i;
export const fontLTBukraRegex = /29ltbukra|29lt|bukra|ltbukra|29bukra|29ltbukra|29lt\s?bukra/i;

const DynamicBanner = ({ locale, content, className }: IProps) => {
  const currency = getCurrencyCode(locale);
  const navigationEnabled = useMemo(() => (content?.items ?? [])?.length > 1, [content?.items]);

  const items = content?.items || [];
  const delay = content?.duration ?? 4;
  // const speed = content?.animationSpeed ?? 0.02;

  // A little hack. @font-face not working.
  const is29LTBukraFont = content?.fontFace?.trim()
    ? fontLTBukraRegex.test(content.fontFace.trim().toLowerCase())
    : false;

  const isLufgaFont = content?.fontFace?.trim()
    ? fontLufgaRegex.test(content.fontFace.trim().toLowerCase())
    : false;

  const handleBetBackGroundStyle = () => {
    if (content?.background?.type === "image")
      return { background: `url("${content?.background?.backgroundUrl}")` };

    if (content?.background?.type === "color")
      return { background: content?.background?.backgroundColor ?? "rgba(194, 212, 220, 1)" };

    return {};
  };

  // console.log("This is dynamic banner content", {
  //   content,
  //   isLufgaFont,
  //   is29LTBukraFont,
  // });

  return (
    <div
      id="dynamic-banner"
      style={{ ...handleBetBackGroundStyle(), color: content?.color }}
      className={cn(
        "fixed top-0 z-[12] flex h-[40px] w-full items-center justify-center gap-4 px-7 py-2 md:h-[50px] md:gap-8",
        className,
      )}
    >
      {/* <DummyClientConsoleComp data={content} /> */}
      {hasArrayElements(items) && (
        <Carousel
          parentClasses="w-full"
          carouselOptions={{ align: "center", loop: true }}
          {...(content?.sticky && {
            autoPlay: {
              playOnInit: true,
              delay: delay * 1000,
              stopOnInteraction: true,
            },
          })}
          {...(navigationEnabled && {
            controls: {
              left: {
                content: (
                  <button className="hidden p-2 md:block">
                    <IconChevronLeft color={content?.color} style={{ color: content?.color }} />
                  </button>
                ),
              },
              right: {
                content: (
                  <div className="hidden p-2 md:block">
                    <IconChevronRight color={content?.color} style={{ color: content?.color }} />
                  </div>
                ),
              },
            },
          })}
          slides={items.map((item) => ({
            slideClasses: "basis-full flex justify-center",
            content:
              item.link?.type === "external" ? (
                <a
                  href={constructUrl(item.link, locale)}
                  target="_blank"
                  style={{
                    color: content?.color,
                    fontSize: content?.fontSize ?? "",
                    justifyContent: content?.textAlign ?? "",
                    ...(is29LTBukraFont
                      ? fontLTBukra.style
                      : isLufgaFont
                        ? lufga.style
                        : {
                            fontFamily: content?.fontFace
                              ? `"${content.fontFace}", sans-serif`
                              : "",
                          }),
                  }}
                >
                  {renderTextWithCurrencyPlaceholder(item?.title, currency)}
                </a>
              ) : (
                <CLink
                  href={constructUrl(item.link, locale)}
                  style={{
                    color: content?.color,
                    fontSize: content?.fontSize ?? "",
                    justifyContent: content?.textAlign ?? "",
                    ...(is29LTBukraFont
                      ? fontLTBukra.style
                      : isLufgaFont
                        ? lufga.style
                        : {
                            fontFamily: content?.fontFace
                              ? `"${content.fontFace}", sans-serif`
                              : "",
                          }),
                  }}
                >
                  {renderTextWithCurrencyPlaceholder(item?.title, currency)}
                </CLink>
              ),
          }))}
        />
      )}
    </div>
  );
};

export default DynamicBanner;

const renderTextWithCurrencyPlaceholder = (
  text: any,
  currencyCode: string,
): ReactNode[] => {
  if (!text || typeof text !== "string") return [text];
  
  // Extremely permissive regex: matches {{CURRENCY_ICON}}, {{ CURRENCY_ICON_SAR }}, {{Currency_Icon}} etc.
  const regex = /{{\s*CURRENCY_ICON(?:_([A-Z]{3}))?\s*}}/i;
  const globalRegex = /{{\s*CURRENCY_ICON(?:_([A-Z]{3}))?\s*}}/gi;

  const hasMatch = regex.test(text);
  
  console.log("DynamicBanner Regex Test:", { 
    text: text, 
    hasMatch, 
    currencyCode,
    charCodes: Array.from(text).map(c => c.charCodeAt(0)) 
  });

  if (!hasMatch) {
    return [text];
  }

  const parts = text.split(globalRegex);
  const result: ReactNode[] = [];

  for (let i = 0; i < parts.length; i += 2) {
    const textPart = parts[i];
    if (textPart !== undefined && textPart !== "") {
      result.push(textPart);
    }

    if (i + 1 < parts.length) {
      const specificCurrency = parts[i + 1]?.toUpperCase();
      result.push(
        <CurrencyIcon key={i} currency={specificCurrency || currencyCode} />,
      );
    }
  }

  return result;
};
